import { App, Descriptions, Divider, Drawer, Image, List, Spin, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { REPORT_STATUS_LABELS } from '@/constants/report';
import { ReportProjectWorkItemsDisplay } from '@/components/report/ReportProjectWorkItemsDisplay';
import { ApiError } from '@/services/api/client';
import * as commentsApi from '@/services/api/comments';
import * as reportsApi from '@/services/api/reports';
import * as uploadsApi from '@/services/api/uploads';
import type { ReportComment, ReportRecord } from '@/types/report';

interface ReportDetailDrawerProps {
  open: boolean;
  reportId: string | null;
  departmentName?: string;
  onClose: () => void;
}

export function ReportDetailDrawer({
  open,
  reportId,
  departmentName,
  onClose,
}: ReportDetailDrawerProps) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReportRecord | null>(null);
  const [comments, setComments] = useState<ReportComment[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !reportId) {
      return;
    }

    const load = async () => {
      setLoading(true);
      setReport(null);
      setComments([]);
      setImageUrls({});

      try {
        const [detail, commentList] = await Promise.all([
          reportsApi.fetchReport(reportId),
          commentsApi.fetchCommentsByReport(reportId),
        ]);
        setReport(detail);
        setComments(commentList.length > 0 ? commentList : detail.comments);

        const urlEntries = await Promise.all(
          detail.images.map(async (image) => {
            try {
              const { url } = await uploadsApi.fetchReportImageAccessUrl(image.id);
              return [image.id, url] as const;
            } catch {
              return [image.id, ''] as const;
            }
          }),
        );
        setImageUrls(Object.fromEntries(urlEntries));
      } catch (error) {
        const text = error instanceof ApiError ? error.message : '加载日报详情失败';
        message.error(text);
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [message, open, reportId]);

  return (
    <Drawer title="日报详情" open={open} onClose={onClose} width={640} destroyOnClose>
      <Spin spinning={loading}>
        {report ? (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="日报日期">
                {new Date(report.reportDate).toLocaleDateString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label="提交人">
                {report.user.name}（{report.user.phone}）
              </Descriptions.Item>
              <Descriptions.Item label="部门">{departmentName ?? report.user.departmentId}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag>{REPORT_STATUS_LABELS[report.status]}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="今日工作">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.todayWork}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="明日计划">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.tomorrowPlan || '—'}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="问题备注">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.problemRemark || '—'}
                </Typography.Paragraph>
              </Descriptions.Item>
            </Descriptions>

            <ReportProjectWorkItemsDisplay report={report} />

            {report.images.length > 0 ? (
              <>
                <Divider orientation="left" plain>
                  图片（{report.images.length}）
                </Divider>
                <Image.PreviewGroup>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {report.images.map((image) => (
                      <Image
                        key={image.id}
                        width={120}
                        height={120}
                        style={{ objectFit: 'cover' }}
                        src={imageUrls[image.id] || undefined}
                        alt={image.originalName}
                        fallback="加载失败"
                      />
                    ))}
                  </div>
                </Image.PreviewGroup>
              </>
            ) : null}

            <Divider orientation="left" plain>
              领导点评（{comments.length}）
            </Divider>
            {comments.length > 0 ? (
              <List
                size="small"
                dataSource={comments}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <SpaceBetween>
                          <span>点评 #{item.id}</span>
                          <Tag color={item.readStatus === 'READ' ? 'default' : 'blue'}>
                            {item.readStatus === 'READ' ? '已读' : '未读'}
                          </Tag>
                        </SpaceBetween>
                      }
                      description={
                        <>
                          <div>{new Date(item.createdAt).toLocaleString('zh-CN')}</div>
                          <Typography.Paragraph style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                            {item.content}
                          </Typography.Paragraph>
                        </>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Typography.Text type="secondary">暂无点评</Typography.Text>
            )}
          </>
        ) : (
          !loading && <Typography.Text type="secondary">暂无数据</Typography.Text>
        )}
      </Spin>
    </Drawer>
  );
}

function SpaceBetween({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{children}</div>
  );
}
